import {
  IFailedCustomMappingRecord,
  ITranslationMap,
  type ICustomMappingSourceOption,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  ServiceProviderHandler,
  TreatmentConfiguration,
  toINamedDocuments,
} from '@principle-theorem/principle-core';
import { type ITreatmentConfiguration } from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  IBlobFilenamePair,
  WithPrimaryKey,
  XSLXImporterExporter,
  asyncForEach,
  isSameRef,
  multiSortBy$,
  nameSorter,
  snapshot,
  type INamedDocument,
  type WithRef,
} from '@principle-theorem/shared';
import { compact, sortBy, uniqBy } from 'lodash';
import { BaseCustomMappingHandler } from '../../../base-custom-mapping-handler';
import { IBaseMigrationItemCode } from '../../../interfaces';
import { ITEM_CODE_TO_TREATMENT_DEFAULT_MAPPINGS } from '../../../mappings/ada-code-mappings';
import { ITEM_CODE_TO_TREATMENT_MAPPING } from '../../../mappings/base-item-code-to-treatment';
import { ItemCodeToTreatmentToXSLX } from '../../../mappings/item-code-to-treatment-to-xlsx';
import { XLSXToItemCodeToTreatment } from '../../../mappings/xlsx-to-item-code-to-treatment';
import { TranslationMapHandler } from '../../../translation-map';
import { FeeScheduleSourceEntity } from '../../source/entities/fee-schedule';
import { PracticeMigration } from '../../../practice-migrations';

export class PraktikaItemCodeToTreatmentMappingHandler extends BaseCustomMappingHandler<ITreatmentConfiguration> {
  customMapping = ITEM_CODE_TO_TREATMENT_MAPPING;

  async getSourceOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<ICustomMappingSourceOption[]> {
    const itemCodes = await this._getItemCodeOptions(migration);
    return itemCodes.map((record) => ({
      label: compact([record.itemCode, record.description]).join(' - '),
      value: record.id.toString(),
    }));
  }

  async getDestinationOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<INamedDocument<ITreatmentConfiguration>[]> {
    return snapshot(
      TreatmentConfiguration.all$(migration.configuration.brand).pipe(
        toINamedDocuments(),
        multiSortBy$(nameSorter())
      )
    );
  }

  async getMappingBlob(
    migration: WithRef<IPracticeMigration>
  ): Promise<IBlobFilenamePair> {
    const { fileName, itemCodes, translator } =
      await this._getExporterData(migration);

    return new XSLXImporterExporter().getBlob(fileName, itemCodes, translator);
  }

  async downloadMapping(migration: WithRef<IPracticeMigration>): Promise<void> {
    const { fileName, itemCodes, translator } =
      await this._getExporterData(migration);

    await new XSLXImporterExporter().download(fileName, itemCodes, translator);
  }

  async uploadMapping(
    migration: WithRef<IPracticeMigration>,
    file: File
  ): Promise<void> {
    const items = await new XSLXImporterExporter().parse(
      file,
      new XLSXToItemCodeToTreatment()
    );

    const translationMap = new TranslationMapHandler(migration);
    const records = await this.getRecords(translationMap);
    await asyncForEach(records, (record) =>
      translationMap.deleteRecord(record)
    );

    const sourceOptions = await this.getSourceOptions(migration);
    const treatmentConfigurations = await this.getDestinationOptions(migration);

    const failedRecords: IFailedCustomMappingRecord[] = [];

    await asyncForEach(items, async (item) => {
      const code = item.code?.trim();
      if (!code) {
        return;
      }
      const sourceOption = sourceOptions.find((opt) => opt.value === code);
      const label = item.description
        ? `${item.code} - ${item.description}`
        : item.code;
      if (!sourceOption && item.mapTo) {
        failedRecords.push({
          sourceIdentifier: code,
          sourceLabel: label,
          reason: 'Item code not found in source options',
        });
        return;
      }
      if (!sourceOption || !item.mapTo) {
        return;
      }
      const treatmentConfiguration = treatmentConfigurations.find(
        (configuration) =>
          configuration.name.trim().toLowerCase() ===
          item.mapTo.trim().toLowerCase()
      );
      if (!treatmentConfiguration) {
        // eslint-disable-next-line no-console
        console.error(
          `Mapping error: ${this.customMapping.metadata.label} - Couldn't find treatment configuration for item`,
          item
        );
        failedRecords.push({
          sourceIdentifier: sourceOption.value,
          sourceLabel: label,
          reason: `Destination identifier "${item.mapTo?.trim()}" not found`,
        });
        return;
      }
      await this.upsertRecord(
        {
          destinationIdentifier: treatmentConfiguration.ref,
          sourceIdentifier: sourceOption.value,
          sourceLabel: sourceOption.label,
        },
        translationMap
      );
    });

    const customMapping = await snapshot(
      PracticeMigration.findCustomMapping$(migration, this.customMapping)
    );
    if (!customMapping) {
      throw new Error('Custom mapping not found for updating failed records');
    }
    await Firestore.patchDoc(customMapping.ref, {
      failedRecords,
    });
  }

  private async _getExporterData(
    migration: WithRef<IPracticeMigration>
  ): Promise<{
    fileName: string;
    itemCodes: IBaseMigrationItemCode[];
    translator: ItemCodeToTreatmentToXSLX<IBaseMigrationItemCode>;
  }> {
    const fileName = this.getFileName();
    const itemCodes = await this._getItemCodeOptions(migration);
    const translationMap = new TranslationMapHandler(migration);
    const translator = new ItemCodeToTreatmentToXSLX(
      await this.getDestinationOptions(migration),
      await this.getRecords(translationMap),
      ITEM_CODE_TO_TREATMENT_DEFAULT_MAPPINGS
    );
    return { fileName, itemCodes, translator };
  }

  private async _getItemCodeOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<IBaseMigrationItemCode[]> {
    const feeScheduleSource = new FeeScheduleSourceEntity();
    const records = await feeScheduleSource
      .getAllRecords$(migration)
      .toPromise();
    const itemCodes = records
      .map((record) => {
        return record.data.items;
      })
      .flat()
      .map((item) => ({
        id: item.custom_code || item.code,
        itemCode: item.custom_code || item.code,
        description:
          item.custom_description ||
          ServiceProviderHandler.findServiceCode(item.code)?.description ||
          '',
      }));
    return uniqBy(
      sortBy(
        itemCodes.filter((record) =>
          ServiceProviderHandler.findServiceCode(record.itemCode)
        ),
        'itemCode'
      ),
      'itemCode'
    );
  }
}

export function resolveMappedTreatmentConfiguration(
  itemCodeId: string,
  treatmentConfigurationMappings: WithPrimaryKey<
    ITranslationMap<ITreatmentConfiguration>
  >[],
  defaultTreatmentConfiguration: WithRef<ITreatmentConfiguration>,
  treatmentConfigurations: WithRef<ITreatmentConfiguration>[]
): WithRef<ITreatmentConfiguration> {
  const mappedTreatment = treatmentConfigurationMappings.find(
    (mapping) => mapping.sourceIdentifier === itemCodeId
  );
  return mappedTreatment?.destinationIdentifier
    ? treatmentConfigurations.find((treatmentConfiguration) =>
        isSameRef(treatmentConfiguration, mappedTreatment.destinationIdentifier)
      ) ?? defaultTreatmentConfiguration
    : defaultTreatmentConfiguration;
}
